import clsx from "clsx";
import { X } from "lucide-react";
import type { ComponentType, ReactNode, SVGProps } from "react";
import { NavLink } from "react-router-dom";

type SidebarIcon = ComponentType<
  SVGProps<SVGSVGElement> & {
    className?: string;
    size?: number | string;
  }
>;

export interface SidebarNavProps {
  ariaLabel: string;
  children: ReactNode;
  className?: string;
}

export const SidebarNav = ({
  ariaLabel,
  children,
  className,
}: SidebarNavProps) => (
  <nav
    aria-label={ariaLabel}
    className={clsx("space-y-1", className)}
    data-sidebar-navigation
  >
    {children}
  </nav>
);

export interface SidebarNavItemProps {
  to: string;
  icon: SidebarIcon;
  children: ReactNode;
  end?: boolean;
  onNavigate?: () => void;
  className?: string;
}

export const SidebarNavItem = ({
  to,
  icon: Icon,
  children,
  end = false,
  onNavigate,
  className,
}: SidebarNavItemProps) => (
  <NavLink
    to={to}
    end={end}
    onClick={onNavigate}
    className={({ isActive }) =>
      clsx(
        "flex min-h-row-compact items-center gap-3 rounded-action px-3 py-2 text-body font-medium transition-colors duration-fast ease-standard focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary",
        isActive
          ? "bg-primary-subtle text-content-accent"
          : "text-content-secondary hover:bg-surface-secondary hover:text-content-primary",
        className,
      )
    }
  >
    <Icon
      aria-hidden="true"
      className="h-4 w-4 shrink-0"
      focusable="false"
      size={16}
      strokeWidth={1.75}
    />
    <span data-sidebar-label>{children}</span>
  </NavLink>
);

export interface SidebarCloseButtonProps {
  ariaLabel: string;
  onClose: () => void;
}

export const SidebarCloseButton = ({
  ariaLabel,
  onClose,
}: SidebarCloseButtonProps) => (
  <button
    type="button"
    aria-label={ariaLabel}
    className="absolute right-3 top-3 z-10 rounded-action bg-surface-primary p-2 text-content-secondary shadow-control transition-colors duration-fast ease-standard hover:bg-surface-secondary hover:text-content-primary focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
    onClick={onClose}
  >
    <X aria-hidden="true" className="h-4 w-4" strokeWidth={1.75} />
  </button>
);

export default SidebarNav;
